import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Printer, X, QrCode } from 'lucide-react';

export default function QRLabelPrint({ assets, open, onClose }) {
  const { t } = useTranslation();
  const printRef = useRef(null);
  
  // Get BASE_URL from env or use relative path
  const baseUrl = process.env.REACT_APP_QR_BASE_URL || 
                  process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || 
                  window.location.origin;

  const generateAssetUrl = (assetId) => {
    return `${baseUrl}/assets/${assetId}`;
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    // Collect QR SVG HTML from the preview elements
    const qrSvgMap = {};
    assets.forEach(asset => {
      const el = document.querySelector('[data-asset-id="' + asset.id + '"] svg');
      qrSvgMap[asset.id] = el ? el.outerHTML : '';
    });
    
    const printWindow = window.open('', '_blank');
    const labelsHtml = assets.map(asset => {
      const serialCode = asset.serial || (asset.id ? asset.id.slice(0, 8) : '—');
      const branchName = asset.branch_name || '';
      const qrSvg = qrSvgMap[asset.id] || '';
      return '<div class="label">' +
        '<svg class="label-logo" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M15.5 7.5C15.5 9.433 13.933 11 12 11C10.067 11 8.5 9.433 8.5 7.5C8.5 5.567 10.067 4 12 4C13.933 4 15.5 5.567 15.5 7.5Z" fill="#e7ae3a"/>' +
        '<path d="M12 13C8.13 13 5 16.13 5 20H19C19 16.13 15.87 13 12 13Z" fill="#e7ae3a"/>' +
        '</svg>' +
        '<div class="label-name">' + asset.name + '</div>' +
        '<div class="label-code">' + serialCode + '</div>' +
        '<div class="label-qr">' + qrSvg + '</div>' +
        '<div class="label-branch">' + branchName + '</div>' +
        '</div>';
    }).join('');
    
    printWindow.document.write(
      '<!DOCTYPE html><html><head><title>QR Labels - Unknown Escape Game</title><style>' +
      '* { margin: 0; padding: 0; box-sizing: border-box; }' +
      'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; background: white; padding: 10mm; }' +
      '.label-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5mm; }' +
      '.label { border: 1px solid #ddd; border-radius: 4px; padding: 4mm; page-break-inside: avoid; text-align: center; background: white; }' +
      '.label-logo { width: 24px; height: 24px; margin-bottom: 2mm; }' +
      '.label-name { font-size: 10pt; font-weight: 600; margin-bottom: 1mm; line-height: 1.2; max-height: 2.4em; overflow: hidden; }' +
      '.label-code { font-family: monospace; font-size: 9pt; color: #666; margin-bottom: 2mm; }' +
      '.label-qr { display: flex; justify-content: center; margin-bottom: 2mm; }' +
      '.label-qr svg { width: 60px !important; height: 60px !important; }' +
      '.label-branch { font-size: 7pt; color: #888; }' +
      '@media print { body { padding: 5mm; } .label { break-inside: avoid; } }' +
      '</style></head><body>' +
      '<div class="label-grid">' + labelsHtml + '</div>' +
      '<script>window.onload = function() { window.print(); }</script>' +
      '</body></html>'
    );
    printWindow.document.close();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-brand" />
            {t('qr.printLabels')}
          </DialogTitle>
          <DialogDescription>
            {assets.length} {t('qr.assetsSelected')}. {t('qr.printInstructions')}
          </DialogDescription>
        </DialogHeader>

        {assets.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            <QrCode className="w-12 h-12 mx-auto opacity-30 mb-4" />
            <p>{t('qr.noAssetsSelected')}</p>
          </div>
        ) : (
          <>
            {/* Preview */}
            <div 
              ref={printRef} 
              className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 bg-white rounded-lg border border-border"
            >
              {assets.map(asset => (
                <div 
                  key={asset.id}
                  className="border border-gray-200 rounded-lg p-4 text-center bg-white"
                  data-asset-id={asset.id}
                >
                  {/* Logo placeholder */}
                  <div className="w-8 h-8 mx-auto mb-2 rounded bg-brand/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#e7ae3a">
                      <circle cx="12" cy="8" r="4"/>
                      <path d="M12 13c-4 0-7 3-7 7h14c0-4-3-7-7-7z"/>
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 truncate mb-1">{asset.name}</p>
                  <p className="text-xs font-mono text-gray-500 mb-2">{asset.serial || asset.id?.slice(0, 8)}</p>
                  <div className="flex justify-center mb-2">
                    <QRCodeSVG 
                      value={generateAssetUrl(asset.id)}
                      size={80}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                  <p className="text-xs text-gray-400">{asset.branch_name}</p>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button variant="outline" onClick={onClose}>
                <X className="w-4 h-4 me-2" />
                {t('common.close')}
              </Button>
              <Button onClick={handlePrint} className="bg-brand hover:bg-brand-hover text-brand-foreground">
                <Printer className="w-4 h-4 me-2" />
                {t('qr.printView')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
